/**
 * EmailWorkerDashboard - Dashboard des actions email en attente
 * Affiche les actions détectées par l'agent, permet de valider/rejeter
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap, Mail, Check, X, RefreshCw, Loader2, Clock, AlertCircle,
  Play, Settings, FolderOpen, FileText, Link, Reply, CheckCheck,
  ChevronDown, ChevronUp, MailOpen, History, Eye, EyeOff, RotateCcw,
  Archive,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../components/ui/select';
import { toast } from 'sonner';
import { getTenantHeaders } from '../../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Icônes par type d'action
const ACTION_ICONS = {
  update_dossier_status: Check,
  update_dossier_field: FileText,
  link_email_to_dossier: Link,
  draft_reply: Reply,
  open_anah_portal: FolderOpen,
  archive_pdf_to_dossier: Archive,
};

const ACTION_COLORS = {
  update_dossier_status: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
  update_dossier_field: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
  link_email_to_dossier: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
  draft_reply: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
  open_anah_portal: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
  archive_pdf_to_dossier: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400',
};

const STATUS_BADGES = {
  pending: { label: 'En attente', class: 'bg-amber-500/20 text-amber-400' },
  executed: { label: 'Exécuté', class: 'bg-emerald-500/20 text-emerald-400' },
  rejected: { label: 'Rejeté', class: 'bg-red-500/20 text-red-400' },
  failed: { label: 'Échec', class: 'bg-red-500/20 text-red-400' },
};

const EmailWorkerDashboard = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [actions, setActions] = useState([]);
  const [processedEmails, setProcessedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [showConfig, setShowConfig] = useState(false);
  const [activeTab, setActiveTab] = useState('actions'); // 'actions' ou 'history'

  const headers = useCallback(() => {
    const token = localStorage.getItem('session_token');
    return {
      ...getTenantHeaders(),
      ...(token && { "Authorization": `Bearer ${token}` }),
      'Content-Type': 'application/json',
    };
  }, []);

  // Charger la config et les stats
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, statsRes, actionsRes, emailsRes] = await Promise.all([
        fetch(`${API}/api/webmail/worker/config`, { headers: headers() }),
        fetch(`${API}/api/webmail/worker/stats`, { headers: headers() }),
        fetch(`${API}/api/webmail/pending-actions?status=${statusFilter}`, { headers: headers() }),
        fetch(`${API}/api/webmail/worker/processed-emails?limit=100`, { headers: headers() }),
      ]);

      if (configRes.ok) {
        const data = await configRes.json();
        setConfig(data.config);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.stats);
      }
      if (actionsRes.ok) {
        const data = await actionsRes.json();
        setActions(data.actions || []);
      }
      if (emailsRes.ok) {
        const data = await emailsRes.json();
        setProcessedEmails(data.emails || []);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  // Sauvegarder la config
  const saveConfig = async (newConfig) => {
    setSavingConfig(true);
    try {
      const res = await fetch(`${API}/api/webmail/worker/config`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(newConfig),
      });
      if (res.ok) {
        const data = await res.json();
        setConfig({ ...config, ...data.config });
        toast.success('Configuration sauvegardée');
      }
    } catch (err) {
      toast.error('Erreur sauvegarde');
    } finally {
      setSavingConfig(false);
    }
  };

  // Déclencher un scan
  const triggerScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API}/api/webmail/worker/scan`, {
        method: 'POST',
        headers: headers(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Scan terminé: ${data.emails_processed || 0} emails traités, ${data.actions_created || 0} actions créées`);
        loadData();
      } else {
        toast.error(data.error || 'Erreur lors du scan');
      }
    } catch (err) {
      toast.error('Erreur lors du scan');
    } finally {
      setScanning(false);
    }
  };

  // Approuver une action
  const approveAction = async (actionId, approve = true) => {
    try {
      const res = await fetch(`${API}/api/webmail/pending-actions/approve`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ action_id: actionId, approve }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        loadData();
      }
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // Réessayer une action en échec
  const retryAction = async (actionId) => {
    try {
      const res = await fetch(`${API}/api/webmail/pending-actions/${actionId}/retry`, {
        method: 'POST',
        headers: headers(),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Action réessayée avec succès');
      } else {
        toast.error(data.failure_reason || 'Réessai échoué');
      }
      loadData();
    } catch (err) {
      toast.error('Erreur réseau');
    }
  };

  // Approuver toutes les actions
  const approveAll = async () => {
    if (!window.confirm('Approuver toutes les actions en attente ?')) return;
    try {
      const res = await fetch(`${API}/api/webmail/pending-actions/approve-all`, {
        method: 'POST',
        headers: headers(),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.approved} actions exécutées`);
        loadData();
      }
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="email-worker-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            Agent Email IA
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Lecture automatique et actions intelligentes sur les emails
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowConfig(!showConfig)}
            className="text-xs"
            data-testid="config-toggle"
          >
            <Settings className="w-3.5 h-3.5 mr-1" />
            Configuration
            {showConfig ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={triggerScan}
            disabled={scanning}
            className="text-xs"
            data-testid="scan-btn"
          >
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
            Scanner maintenant
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500">Statut</span>
            <div className={`w-2 h-2 rounded-full ${config?.enabled ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
          </div>
          <p className="text-lg font-semibold text-white mt-1">
            {config?.enabled ? 'Actif' : 'Inactif'}
          </p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
          <span className="text-xs text-zinc-500">Emails traités</span>
          <p className="text-lg font-semibold text-white mt-1">{stats?.emails_processed || 0}</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
          <span className="text-xs text-zinc-500">Actions créées</span>
          <p className="text-lg font-semibold text-white mt-1">{stats?.actions_created || 0}</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
          <span className="text-xs text-zinc-500">En attente</span>
          <p className="text-lg font-semibold text-amber-400 mt-1">{stats?.pending_actions || 0}</p>
        </div>
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50 space-y-4" data-testid="config-panel">
          <h3 className="text-sm font-semibold text-zinc-300">Configuration de l'agent</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm text-zinc-300">Activer l'agent</label>
                <p className="text-xs text-zinc-500">Scan automatique des emails</p>
              </div>
              <Switch
                checked={config?.enabled || false}
                onCheckedChange={(v) => saveConfig({ ...config, enabled: v })}
                data-testid="enable-switch"
              />
            </div>
            
            <div>
              <label className="text-sm text-zinc-300 block mb-1">Fréquence (minutes)</label>
              <Select
                value={String(config?.interval_minutes || 60)}
                onValueChange={(v) => saveConfig({ ...config, interval_minutes: parseInt(v) })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 min</SelectItem>
                  <SelectItem value="15">15 min</SelectItem>
                  <SelectItem value="30">30 min</SelectItem>
                  <SelectItem value="60">1 heure</SelectItem>
                  <SelectItem value="120">2 heures</SelectItem>
                  <SelectItem value="360">6 heures</SelectItem>
                  <SelectItem value="720">12 heures</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm text-zinc-300">Actions simples auto</label>
                <p className="text-xs text-zinc-500">Exécuter automatiquement les liens email-dossier</p>
              </div>
              <Switch
                checked={config?.auto_execute_simple || false}
                onCheckedChange={(v) => saveConfig({ ...config, auto_execute_simple: v })}
              />
            </div>
          </div>
          
          {config?.last_run && (
            <p className="text-xs text-zinc-500">
              Dernier scan: {formatDate(config.last_run)}
            </p>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        <button
          onClick={() => setActiveTab('actions')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === 'actions' 
              ? 'text-white' 
              : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <span className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Actions
            {(stats?.pending_actions || 0) > 0 && (
              <Badge className="text-[10px] bg-amber-500/20 text-amber-400 ml-1">
                {stats?.pending_actions}
              </Badge>
            )}
          </span>
          {activeTab === 'actions' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-400" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === 'history' 
              ? 'text-white' 
              : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <span className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Historique emails
            <Badge className="text-[10px] bg-zinc-700 text-zinc-300 ml-1">
              {processedEmails.length}
            </Badge>
          </span>
          {activeTab === 'history' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400" />
          )}
        </button>
      </div>

      {/* Actions Tab */}
      {activeTab === 'actions' && (
      <div className="bg-zinc-900/50 rounded-lg border border-zinc-800">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-white">Actions détectées</h3>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-7 w-32 text-xs bg-zinc-800 border-zinc-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="executed">Exécutées</SelectItem>
                <SelectItem value="failed">Échecs</SelectItem>
                <SelectItem value="rejected">Rejetées</SelectItem>
                <SelectItem value="all">Toutes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            {statusFilter === 'pending' && actions.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={approveAll}
                className="text-xs bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                data-testid="approve-all-btn"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Tout approuver
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={loadData} className="h-7 w-7 p-0">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* AG-04 #8 — Radix ScrollArea exige une hauteur définie (h-) pour
            que le viewport interne (h-full) prenne la hauteur. max-h- seul
            laisse le viewport à 0px → contenu coupé sans scroll. */}
        <ScrollArea className="h-[600px]">
          {actions.length === 0 ? (
            <div className="text-center py-12">
              <MailOpen className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Aucune action {statusFilter === 'pending' ? 'en attente' : ''}</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {actions.map((action) => {
                const Icon = ACTION_ICONS[action.action?.type] || Zap;
                const colorClass = ACTION_COLORS[action.action?.type] || 'bg-zinc-700/50 border-zinc-600 text-zinc-300';
                const statusBadge = STATUS_BADGES[action.status] || STATUS_BADGES.pending;
                
                return (
                  <div key={action.id} className="px-4 py-3 hover:bg-zinc-800/30" data-testid={`action-${action.id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg border ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-white truncate">
                            {action.action?.label || 'Action'}
                          </span>
                          <Badge className={`text-[10px] ${statusBadge.class}`}>
                            {statusBadge.label}
                          </Badge>
                          {action.priority === 'high' && (
                            <Badge className="text-[10px] bg-red-500/20 text-red-400">Urgent</Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-zinc-500">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{action.email_subject}</span>
                          <span>—</span>
                          <span>{action.email_from}</span>
                        </div>
                        
                        {action.matched_dossier && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-cyan-400">
                            <FolderOpen className="w-3 h-3" />
                            <span>Dossier: {action.matched_dossier.reference}</span>
                            {action.matched_dossier.client && (
                              <span className="text-zinc-500">— {action.matched_dossier.client}</span>
                            )}
                            {action.matched_dossier.match_method && (
                              <span className="text-zinc-600 ml-1">
                                · {action.matched_dossier.match_method.split(':')[0]}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {action.extracted_data?.resume && (
                          <p className="text-xs text-zinc-400 mt-1 italic">
                            {action.extracted_data.resume}
                          </p>
                        )}

                        {action.status === 'failed' && action.failure_reason && (
                          <div className="flex items-start gap-1 mt-1 text-xs text-red-400">
                            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                            <span>{action.failure_reason}</span>
                            {action.retry_count > 0 && (
                              <span className="text-zinc-600 ml-1">
                                ({action.retry_count} essai{action.retry_count > 1 ? 's' : ''})
                              </span>
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-1 mt-1 text-[11px] text-zinc-600">
                          <Clock className="w-3 h-3" />
                          <span>{formatDate(action.created_at)}</span>
                        </div>
                      </div>

                      {action.status === 'pending' && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => approveAction(action.id, true)}
                            className="h-7 w-7 p-0 text-emerald-400 hover:bg-emerald-500/10"
                            data-testid={`approve-${action.id}`}
                            aria-label="Approuver l'action"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => approveAction(action.id, false)}
                            className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10"
                            data-testid={`reject-${action.id}`}
                            aria-label="Rejeter l'action"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {action.status === 'failed' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => retryAction(action.id)}
                          className="h-7 px-2 text-xs text-amber-400 hover:bg-amber-500/10"
                          data-testid={`retry-${action.id}`}
                          aria-label="Réessayer l'action"
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1" />
                          Réessayer
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>
      )}

      {/* History Tab - Emails traités */}
      {activeTab === 'history' && (
      <div className="bg-zinc-900/50 rounded-lg border border-zinc-800">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-white">Emails traités</h3>
            <span className="text-xs text-zinc-500">
              Tous les emails analysés par l'agent
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={loadData} className="h-7 w-7 p-0">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        <ScrollArea className="h-[600px]">
          {processedEmails.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Aucun email traité</p>
              <p className="text-zinc-600 text-xs mt-1">Lancez un scan pour analyser les emails</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {processedEmails.map((email, idx) => (
                <div key={`${email.email_uid}-${idx}`} className="px-4 py-3 hover:bg-zinc-800/30">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg border ${
                      email.has_actions 
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                        : 'bg-zinc-700/30 border-zinc-600 text-zinc-400'
                    }`}>
                      {email.has_actions ? <Zap className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-white truncate">
                          {email.email_subject || '(Sans objet)'}
                        </span>
                        {email.has_actions ? (
                          <Badge className="text-[10px] bg-amber-500/20 text-amber-400">
                            {email.result?.actions_count || 0} action(s)
                          </Badge>
                        ) : (
                          <Badge className="text-[10px] bg-zinc-700 text-zinc-400">
                            Pas d'action
                          </Badge>
                        )}
                        {email.is_read ? (
                          <Eye className="w-3 h-3 text-zinc-500" title="Lu" />
                        ) : (
                          <EyeOff className="w-3 h-3 text-blue-400" title="Non lu" />
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <Mail className="w-3 h-3" />
                        <span>{email.email_from}</span>
                        <span>—</span>
                        <span>{email.email_folder}</span>
                      </div>
                      
                      {email.result?.detected_type && email.result.detected_type !== 'unknown' && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-cyan-400">
                          <FolderOpen className="w-3 h-3" />
                          <span>Type: {email.result.detected_type}</span>
                          {email.result.matched_dossier && (
                            <span className="text-zinc-500">— Dossier: {email.result.matched_dossier.référence}</span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-1 mt-1 text-[11px] text-zinc-600">
                        <Clock className="w-3 h-3" />
                        <span>Traité le {formatDate(email.processed_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
      )}
    </div>
  );
};

export default EmailWorkerDashboard;
